import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { StepContainer } from '@components/Steps/Steps.styles';
import {
  LuxButton,
  LuxDivider,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import { useCallback, useMemo } from 'react';
import { openGoogleReviews, renderStars } from './utils';
import { GoogleReview } from './PracticeCardPage';

interface Props {
  reviews: GoogleReview;
}

export function RatingsCard({ reviews }: Props) {
  const theme = useTheme();
  const { t } = useCustomTranslation();
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const placeId = useOnlineBookingStore(
    state => state.generalInfo?.googlePlaceId,
  );
  const colorConfig = useOnlineBookingStore(
    state => state.generalConfig?.customization,
  );
  const practiceInfo = useOnlineBookingStore(state => state.generalConfig);
  // States for UI
  const visibleReviews = useMemo(
    () => reviews.reviews.slice(0, 3),
    [reviews.reviews],
  );

  const handleOpenAll = useCallback(() => {
    openGoogleReviews(placeId || '');
  }, [placeId]);

  return (
    <>
      {practiceInfo?.practiceInformation?.showRatingAndReview && placeId && (
        <StepContainer>
          <CardTitleWrapper
            borderColor={
              theme.palette.LUX.obTokens.bordersColorNeutralSecondary
            }
          >
            <LuxTypography variant="heading4">
              {t('practiceCard.reviews')}
            </LuxTypography>
            {!isMobile && (
              <LuxButton onClick={handleOpenAll} variant="secondary">
                {t('practiceCard.viewReviews').replace(
                  '{{totalReviewsNumber}}',
                  reviews.totalReviewsNumber.toString(),
                )}
              </LuxButton>
            )}
          </CardTitleWrapper>

          <Stack padding="0.5rem 1rem 1rem 1rem" gap="1rem">
            {visibleReviews.length > 0 && (
              <Stack flexDirection="column" gap="1.5rem" width="100%">
                {visibleReviews.map((r, idx) => (
                  <Stack
                    key={`${r.authorAttribution?.displayName}-${idx}`}
                    gap="0.5rem"
                  >
                    <LuxTypography variant="textMedium">
                      {r.authorAttribution?.displayName}
                    </LuxTypography>
                    <Stack flexDirection={'row'} alignItems={'center'}>
                      {renderStars(
                        r.rating || undefined,
                        colorConfig?.primaryColor,
                      )}
                      <LuxTypography variant="textXs">
                        {r.relativePublishTimeDescription}
                      </LuxTypography>
                    </Stack>
                    {r.text && <LuxTypography>{r.text}</LuxTypography>}
                    {idx < visibleReviews.length - 1 && <LuxDivider />}
                  </Stack>
                ))}
              </Stack>
            )}

            {isMobile && (
              <LuxButton onClick={handleOpenAll} variant="secondary">
                {t('practiceCard.viewReviews').replace(
                  '{{totalReviewsNumber}}',
                  reviews.totalReviewsNumber.toString(),
                )}
              </LuxButton>
            )}
          </Stack>
        </StepContainer>
      )}
    </>
  );
}
